import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  WorkflowTestCapture,
  WorkflowTestInputSpecs,
  WorkflowTestResult,
  CreateStepBody,
  CreateWorkflowBody,
  PublishWorkflowBody,
  UpdateStepBody,
  UpdateWorkflowBody,
  WorkflowDetail,
} from '@vagusx/clients';
import { getAgentComponentsClient, getWorkflowDefnsClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CACHE_TIME_10_MINS } from '@/lib/query-client';

/**
 * Workflow builder queries (ADR-275).
 *
 * THE SERVER HOLDS THE DRAFT. Every edit is saved as it is made, against the
 * revision it was made on, and the response is the whole workflow again —
 * problems and gate facts included — so the editor never computes either
 * itself. A save on a stale revision is refused and surfaces as the global
 * error toast telling the author to reload.
 */

export function useComponentCatalog() {
  return useQuery({
    queryKey: queryKeys.workflowDefns.catalog(),
    queryFn: ({ signal }) => getAgentComponentsClient().getCatalog(signal),
    // The catalog changes with a deploy, not with anything an author does.
    staleTime: CACHE_TIME_10_MINS,
  });
}

export function useWorkflows() {
  return useQuery({
    queryKey: queryKeys.workflowDefns.list(),
    queryFn: ({ signal }) => getWorkflowDefnsClient().list(signal),
  });
}

export function useWorkflow(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workflowDefns.detail(id ?? ''),
    queryFn: ({ signal }) => getWorkflowDefnsClient().getById(id!, signal),
    enabled: !!id,
  });
}

/** How often the test sheet asks whether the report has landed, and for how long. */
const TEST_RESULT_POLL_MS = 2_000;
const TEST_RESULT_POLLS = 30;

/**
 * Everything a finished test call left behind, for a ticket (ADR-309). Asked
 * for when the tester copies or downloads it, never cached: the worker's
 * trace lands a few seconds after the call ends, and a later press should
 * pick it up.
 */
export function useFetchWorkflowTestCapture(
  workflowId: string,
  interactionId: string
): () => Promise<WorkflowTestCapture> {
  const queryClient = useQueryClient();
  return () =>
    queryClient.fetchQuery({
      queryKey: queryKeys.workflowDefns.testCapture(workflowId, interactionId),
      queryFn: ({ signal }) =>
        getWorkflowDefnsClient().testCapture(workflowId, interactionId, signal),
      staleTime: 0,
    });
}

/**
 * What a finished builder test understood and withheld (ADR-306).
 *
 * The worker posts the report as the walk closes, a moment after the tester
 * hangs up, so this asks again until it arrives — and stops after a minute, so
 * a report that never lands leaves a sheet that says so rather than a poll
 * that runs for ever.
 */
export function useWorkflowTestResult(
  workflowId: string,
  interactionId: string | null
): UseQueryResult<WorkflowTestResult> & { gaveUp: boolean } {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.workflowDefns.testResult(workflowId, interactionId ?? '');
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      getWorkflowDefnsClient().testResult(workflowId, interactionId!, signal),
    enabled: !!interactionId,
    // Stops on a report, on an error (a 404 will not become a report), and
    // after a minute of asking.
    refetchInterval: (q) =>
      q.state.data?.report ||
      q.state.status === 'error' ||
      q.state.dataUpdateCount >= TEST_RESULT_POLLS
        ? false
        : TEST_RESULT_POLL_MS,
  });
  const polls = queryClient.getQueryState(queryKey)?.dataUpdateCount ?? 0;
  return {
    ...query,
    /** Asked for a minute and nothing came: the sheet says so instead of waiting. */
    gaveUp: !query.data?.report && polls >= TEST_RESULT_POLLS,
  };
}

/**
 * What a test of this draft — or of one step — asks the tester for, as the
 * API will hold it to (ADR-306). Asked fresh for each test: it depends on the
 * step's config, which the author may just have changed.
 */
export function useWorkflowTestInputs(
  workflowId: string,
  nodeId: string | null,
  /** The test this is for: each new test asks afresh, never from cache. */
  testKey: number,
  enabled: boolean
): UseQueryResult<WorkflowTestInputSpecs> {
  return useQuery({
    queryKey: queryKeys.workflowDefns.testInputs(workflowId, nodeId, testKey),
    queryFn: ({ signal }) =>
      getWorkflowDefnsClient().testInputs(workflowId, nodeId ?? undefined, signal),
    enabled,
    staleTime: 0,
  });
}

/** Put a fresh copy in the cache, so the next edit is made on its revision. */
function useStore(): (detail: WorkflowDetail) => void {
  const queryClient = useQueryClient();
  return (detail) => {
    queryClient.setQueryData(queryKeys.workflowDefns.detail(detail.id), detail);
    void queryClient.invalidateQueries({ queryKey: queryKeys.workflowDefns.list() });
  };
}

export function useCreateWorkflow() {
  const store = useStore();
  return useMutation({
    mutationFn: (body: CreateWorkflowBody) => getWorkflowDefnsClient().create(body),
    onSuccess: store,
  });
}

export function useUpdateWorkflow(id: string) {
  const store = useStore();
  return useMutation({
    mutationFn: (body: UpdateWorkflowBody) => getWorkflowDefnsClient().update(id, body),
    onSuccess: store,
  });
}

/**
 * Publishing refuses while a problem blocks, with every problem as a field
 * error. The dialog shows those itself, so the global toast is suppressed.
 */
export function usePublishWorkflow(id: string) {
  const store = useStore();
  return useMutation({
    mutationFn: (body: PublishWorkflowBody) => getWorkflowDefnsClient().publish(id, body),
    onSuccess: (detail) => {
      store(detail);
      toast.success(`Published version ${detail.activeVersion?.version ?? ''}`.trim());
    },
    meta: { suppressGlobalError: true },
  });
}

export function useArchiveWorkflow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getWorkflowDefnsClient().archive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflowDefns.all });
      toast.success('Workflow deleted');
    },
  });
}

/**
 * A saved step goes into the cached workflow at once, so the next edit is
 * made on its new revision; the workflow is then refetched, because a
 * step's words and settings change its problems and its gate facts.
 */
export function useCreateStep(workflowId: string) {
  return useMutation({
    mutationFn: (body: CreateStepBody) => getWorkflowDefnsClient().createStep(workflowId, body),
  });
}

export function useUpdateStep(workflowId: string) {
  const queryClient = useQueryClient();
  const key = queryKeys.workflowDefns.detail(workflowId);
  return useMutation({
    mutationFn: ({ stepId, body }: { stepId: string; body: UpdateStepBody }) =>
      getWorkflowDefnsClient().updateStep(workflowId, stepId, body),
    onSuccess: (step) => {
      queryClient.setQueryData<WorkflowDetail>(key, (detail) =>
        detail
          ? { ...detail, steps: detail.steps.map((s) => (s.id === step.id ? step : s)) }
          : detail
      );
      return queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
